import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { dailyTotals, reconcileMilkDay } from '@dairy/domain';
import { addDays } from '@dairy/shared';
import { useIsOwner, useSettings } from '@/auth/useSession';
import { Badge, Card, PageHeader, buttonVariants } from '@/components/ui';
import { useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { useMilkRecords, useMilkSales, useUsage } from './useMilk';

/** Screen 5 — Milk history (MR-6): the last 14 days. */
export function MilkHistoryPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const s = useSettings();
  const isOwner = useIsOwner();
  const today = useToday();
  const from = addDays(today, -13);
  const records = useMilkRecords(from, today) ?? [];
  const usage = useUsage(from, today) ?? [];
  const sales = useMilkSales(from, today) ?? [];
  const days = Array.from({ length: 14 }, (_, i) => addDays(today, -i));
  return (
    <div>
      <PageHeader
        title={t('milk.historyTitle')}
        actions={
          <>
            <Link to="/milk/usage" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
              {t('today.milkUsage')}
            </Link>
            {isOwner ? (
              <Link to="/milk/table" className={buttonVariants({ variant: 'secondary', size: 'sm' }) + ' hidden lg:inline-flex'}>
                {t('milk.tableTitle')}
              </Link>
            ) : null}
          </>
        }
      />
      <ul className="space-y-2">
        {days.map((d) => {
          const tot = dailyTotals(records, usage, d);
          const sold = sales.filter((x) => x.date === d).reduce((a, x) => a + (x.milkMl ?? 0), 0);
          const rec = reconcileMilkDay(tot.saleableMl, sold, s.unaccountedMilkThreshold);
          return (
            <li key={d}>
              <Link to={`/milk/day/${d}`}>
                <Card className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{f.date(d)}</span>
                    <span className="text-lg font-bold tabular-nums">{f.liters(tot.producedMl)}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-600">
                    <span>{t('milk.withheld')}: {f.liters(tot.withheldMl)}</span>
                    <span>{t('milk.usage')}: {f.liters(tot.usageMl)}</span>
                    <span className="font-medium text-stone-900">{t('milk.saleable')}: {f.liters(tot.saleableMl)}</span>
                    {tot.saleableMl < 0 ? <Badge tone="red">{t('milk.negativeSaleable')}</Badge> : null}
                    {tot.producedMl > 0 && rec.flag !== 'OK' ? (
                      <Badge tone="amber">{rec.flag === 'OVERSOLD' ? t('milk.oversold') : t('milk.unaccounted', { liters: f.liters(rec.unaccountedMl) })}</Badge>
                    ) : null}
                  </div>
                </Card>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
